import * as React from "react";
import { cn } from "@/lib/utils";

export interface CheckboxProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

/** Clayglass checkbox with clay pressed state when checked. */
const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ className, checked: controlledChecked, defaultChecked = false, onCheckedChange, ...props }, ref) => {
    const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
    const checked = controlledChecked !== undefined ? controlledChecked : internalChecked;

    const toggle = React.useCallback(() => {
      const next = !checked;
      if (controlledChecked === undefined) setInternalChecked(next);
      onCheckedChange?.(next);
    }, [checked, controlledChecked, onCheckedChange]);

    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={checked}
        data-state={checked ? "checked" : "unchecked"}
        onClick={toggle}
        className={cn(
          "peer h-4 w-4 shrink-0 rounded-md border border-primary transition-all",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          checked
            ? "bg-primary text-primary-foreground clay-pressed"
            : "bg-background/50 glass-input",
          className
        )}
        {...props}
      >
        {checked && (
          <span className="flex items-center justify-center text-current">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        )}
      </button>
    );
  }
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
